"use client";

import { ChevronDown } from "lucide-react";
import React from "react";

type FAQItem = {
  question: string;
  answer: string;
};

const faqItems: FAQItem[] = [
  {
    question: "How much do your services cost?",
    answer:
      "Our pricing varies based on the type of project, duration, location, and specific requirements. We offer packages for weddings, portraits, commercial work, and custom projects. Please contact us for a detailed quote tailored to your needs.",
  },
  {
    question: "How far in advance should I book?",
    answer:
      "We recommend booking at least 2-3 months in advance for weddings and 2-4 weeks for portraits and commercial projects. However, we do accommodate last-minute bookings when availability permits. Popular dates (weekends, holidays) tend to book quickly.",
  },
  {
    question: "What is included in your photography packages?",
    answer:
      "Our packages typically include professional photography coverage, post-processing and editing, high-resolution digital files, online gallery access, and usage rights. Specific inclusions vary by package type. We can customize packages to meet your specific needs.",
  },
  {
    question: "How long does it take to receive my photos?",
    answer:
      "Delivery timelines vary by project type: Weddings typically take 4-6 weeks, portraits 2-3 weeks, and commercial projects 1-2 weeks. Rush delivery options are available for an additional fee. We always provide a timeline during our consultation.",
  },
  {
    question: "Do you travel for shoots?",
    answer:
      "Yes! We are based in India and travel worldwide for projects. Travel expenses are calculated separately based on location. We have experience shooting in various locations and can help coordinate travel logistics for destination shoots.",
  },
  {
    question: "What is your cancellation policy?",
    answer:
      "We require a 50% deposit to secure your booking. Cancellations made more than 30 days before the shoot date receive a full refund of the deposit. Cancellations within 30 days are subject to a 50% refund. Rescheduling is preferred and can be arranged based on availability.",
  },
  {
    question: "Can I request specific shots or styles?",
    answer:
      "Absolutely! During our consultation and planning phase, we discuss your vision, style preferences, and any specific shots you want. We create a shot list together to ensure we capture everything important to you.",
  },
  {
    question: "Do you provide raw/unedited photos?",
    answer:
      "We deliver professionally edited, high-resolution images that reflect our signature style. Raw files are not included in standard packages, as our editing process is an integral part of our artistic vision. However, we can discuss options for additional files if needed.",
  },
  {
    question: "What equipment do you use?",
    answer:
      "We use professional-grade cameras, lenses, and lighting equipment from leading brands. We always bring backup equipment to ensure nothing interrupts your shoot. Our technical expertise allows us to adapt to any lighting or environmental conditions.",
  },
  {
    question: "How many photos will I receive?",
    answer:
      "The number of photos varies by project type and duration. For weddings, you can expect 400-800+ edited images. Portrait sessions typically yield 30-50 images, and commercial projects vary based on scope. We focus on quality over quantity, delivering only the best images.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="relative isolate mx-auto w-full px-4 py-12 sm:px-6 sm:py-16 md:py-20 lg:px-8 lg:py-24"
    >
      {/* Fancy gradient background - continues from testimonials bottom left and bottom right */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Base gradient - starts with testimonials' ending colors at top left and top right */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 100% 80% at top left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at top center, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at top right, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at left center, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at right center, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom center, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom right, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              linear-gradient(to bottom left, oklch(0.828 0.189 84.429 / 0.12) 0%, transparent 40%, oklch(0.646 0.222 41.116 / 0.15) 100%),
              oklch(1 0 0)
            `,
          }}
        />

        {/* Animated floating orbs */}
        <div className="bg-chart-1/20 absolute top-20 -left-32 size-96 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse" />
        <div className="bg-chart-4/20 absolute -right-32 bottom-20 size-80 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-1000" />
        <div className="bg-chart-2/15 absolute top-1/2 left-1/2 size-72 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-2000" />

        {/* Subtle mesh gradient overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(to bottom left, transparent 0%, oklch(0.828 0.189 84.429 / 0.05) 30%, oklch(0.646 0.222 41.116 / 0.05) 70%, transparent 100%)",
          }}
        />
      </div>

      <div className="container mx-auto">
        {/* Header */}
        <div className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 mb-12 text-center motion-safe:duration-700 sm:mb-16">
          <span className="text-primary text-xs font-medium tracking-wider uppercase sm:text-sm md:text-base">
            Got Questions?
          </span>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl lg:text-5xl">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-sm leading-relaxed sm:text-base md:text-lg">
            Find answers to common questions about our services, booking process, and what to
            expect.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="mx-auto max-w-3xl space-y-3 sm:space-y-4">
          {faqItems.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="bg-card group motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 rounded-xl border shadow-xs transition-all duration-300 hover:shadow-md motion-safe:duration-700"
                style={{
                  animationDelay: `${idx * 50}ms`,
                  background: `
                    linear-gradient(to bottom left, oklch(0.828 0.189 84.429 / 0.05), oklch(0.646 0.222 41.116 / 0.03)),
                    oklch(1 0 0)
                  `,
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleQuestion(idx)}
                  className="focus-visible:ring-ring flex w-full items-center justify-between gap-4 p-4 text-left transition-all duration-300 focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:p-6"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${idx}`}
                >
                  <h3 className="group-hover:text-primary text-base font-semibold transition-colors duration-300 sm:text-lg md:text-xl">
                    {item.question}
                  </h3>
                  <ChevronDown
                    className={`text-muted-foreground size-5 shrink-0 transition-transform duration-300 sm:size-6 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id={`faq-answer-${idx}`}
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="text-muted-foreground border-t px-4 pt-2 pb-4 text-sm leading-relaxed sm:px-6 sm:pb-6 sm:text-base">
                    {item.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div
          className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 mt-12 text-center motion-safe:duration-700 sm:mt-16"
          style={{
            animationDelay: "500ms",
          }}
        >
          <p className="text-muted-foreground mb-4 text-sm sm:text-base">
            Still have questions? We&apos;d love to hear from you.
          </p>
          <a
            href="#contact"
            className="focus-visible:ring-ring bg-primary text-primary-foreground hover:bg-primary/90 inline-block rounded-md px-6 py-3 text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:px-8 sm:py-3.5 sm:text-base"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
}
